import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Search, Trash2, ReceiptText, Eye, Truck, Printer, Plus, AlertTriangle, Pencil, History, X, Minus } from 'lucide-react';
import api, { fmtIDR, fmtDate, API } from '@/lib/api';
import { toast } from 'sonner';
import EmptyState from '@/components/common/EmptyState';

export default function TransactionsHistory() {
  const [txs, setTxs] = useState([]);
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  // State untuk fitur EDIT transaksi & lihat riwayat edit.
  const [editTx, setEditTx] = useState(null);
  const [historyTx, setHistoryTx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/transactions?limit=500');
      setTxs(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat transaksi');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query) return txs;
    const q = query.toLowerCase();
    return txs.filter((t) =>
      (t.invoice_no || '').toLowerCase().includes(q)
      || (t.customer_name || '').toLowerCase().includes(q)
      || (t.customer_company || '').toLowerCase().includes(q)
      || (t.salesman_name || '').toLowerCase().includes(q)
      || (t.created_by_name || '').toLowerCase().includes(q)
    );
  }, [txs, query]);

  const totalOmset = useMemo(() => filtered.reduce((a, t) => a + (t.total || 0), 0), [filtered]);

  const doDelete = async (tx) => {
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success(`Transaksi ${tx.invoice_no} dihapus. Stok dikembalikan.`);
      setTxs((prev) => prev.filter((x) => x.id !== tx.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus transaksi');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari invoice, customer, salesman, kasir..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="tx-history-search" />
        </div>
        <div className="text-sm text-muted-foreground">
          <b className="tabular-nums">{filtered.length}</b> transaksi • Total omset: <b className="tabular-nums">{fmtIDR(totalOmset)}</b>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm text-muted-foreground">Memuat...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ReceiptText} title="Belum ada transaksi" description="Transaksi POS akan tampil di sini." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="tx-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Kasir</TableHead>
                    <TableHead>Salesman</TableHead>
                    <TableHead>Metode</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.id} data-testid={`tx-history-row-${t.id}`}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{t.invoice_no}</TableCell>
                      <TableCell>
                        <div className="font-medium">{t.customer_name || '-'}</div>
                        {t.customer_company && <div className="text-[10px] text-muted-foreground">{t.customer_company}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{t.created_by_name || '-'}</TableCell>
                      <TableCell className="text-sm">{t.salesman_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-[10px] uppercase">{t.payment_method}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtIDR(t.total)}</TableCell>
                      <TableCell>
                        {t.payment_status === 'paid' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Lunas</Badge>
                        ) : t.is_overdue ? (
                          <Badge variant="destructive" className="text-[10px]">Telat</Badge>
                        ) : t.payment_status === 'partial' ? (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Parsial</Badge>
                        ) : (
                          <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]">Tempo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => setDetail(t)} data-testid={`tx-detail-${t.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" />Detail
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200"
                          onClick={() => setEditTx(t)}
                          data-testid={`tx-edit-${t.id}`}
                          title="Edit transaksi (semua role bisa)"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                          {(t.edit_history?.length || 0) > 0 && (
                            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px]" data-testid={`tx-edit-badge-${t.id}`}>
                              {t.edit_history.length}×
                            </Badge>
                          )}
                        </Button>
                        {(t.edit_history?.length || 0) > 0 && (
                          <Button
                            size="sm" variant="outline"
                            className="text-purple-600 hover:bg-purple-50 border-purple-200"
                            onClick={() => setHistoryTx(t)}
                            data-testid={`tx-history-btn-${t.id}`}
                            title="Lihat riwayat edit"
                          >
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`tx-delete-${t.id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" />Hapus
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`tx-delete-confirm-${t.id}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus transaksi {t.invoice_no}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Transaksi akan dihapus permanen. Stok barang otomatis dikembalikan ke katalog.
                                <br /><br />
                                <b>Customer:</b> {t.customer_name || '-'}<br />
                                <b>Total:</b> {fmtIDR(t.total)}<br />
                                <b>Jumlah item:</b> {(t.items || []).length}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => doDelete(t)} className="bg-rose-600 hover:bg-rose-700" data-testid={`tx-delete-confirm-btn-${t.id}`}>
                                Ya, Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <TransactionDetailDialog tx={detail} onClose={() => setDetail(null)} />
      <EditTransactionDialog
        tx={editTx}
        onClose={() => setEditTx(null)}
        onSaved={() => { setEditTx(null); load(); }}
      />
      <EditHistoryDialog tx={historyTx} onClose={() => setHistoryTx(null)} />
    </div>
  );
}

function TransactionDetailDialog({ tx, onClose }) {
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tx-detail-dialog">
        <DialogHeader>
          <DialogTitle>Detail Transaksi {tx?.invoice_no}</DialogTitle>
        </DialogHeader>
        {tx && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3 space-y-1">
                <div className="text-xs text-muted-foreground">Tanggal</div>
                <div className="font-medium">{fmtDate(tx.created_at)}</div>
              </div>
              <div className="rounded-lg border p-3 space-y-1">
                <div className="text-xs text-muted-foreground">Metode Pembayaran</div>
                <div className="font-medium uppercase">{tx.payment_method}</div>
              </div>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <div className="text-xs text-muted-foreground">Customer</div>
              <div className="font-medium">{tx.customer_name || '-'}</div>
              {tx.customer_company && <div className="text-xs text-muted-foreground">{tx.customer_company}</div>}
              {tx.customer_whatsapp && <div className="text-xs text-muted-foreground">WA: {tx.customer_whatsapp}</div>}
            </div>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Barang</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Harga</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(tx.items || []).map((it, i) => (
                    <TableRow key={`${it.product_id || it.sku || 'item'}-${i}`}>
                      <TableCell>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{it.sku}</div>
                        {(it.discount_percentage || 0) > 0 && (
                          <div className="text-[10px] text-amber-700">Diskon {it.discount_percentage.toFixed(1)}% (asli {fmtIDR(it.original_price)})</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{it.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtIDR(it.unit_price)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtIDR(it.subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-between items-center rounded-lg bg-primary/5 border-2 border-primary/20 p-3">
              <div className="font-semibold">Total</div>
              <div className="text-xl font-heading font-semibold tabular-nums">{fmtIDR(tx.total)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
              <div>Kasir: <b className="text-foreground">{tx.created_by_name || '-'}</b></div>
              <div>Salesman: <b className="text-foreground">{tx.salesman_name || '-'}</b></div>
            </div>
            {tx.notes && <div className="text-xs italic text-muted-foreground">Catatan: {tx.notes}</div>}

            {/* Split Surat Jalan (kapasitas truk). Tersedia untuk semua role yang bisa lihat detail (owner/admin/kasir). */}
            <DeliveryBatchesSection txId={tx.id} invoiceNo={tx.invoice_no} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Section untuk kelola Surat Jalan (Delivery Order) per transaksi.
 * - Menampilkan ringkasan "sudah dikirim vs sisa" per item
 * - List batch pengiriman + tombol cetak PDF per batch
 * - Tombol "Buat Surat Jalan Baru" jika masih ada sisa yang belum dikirim
 * Backend endpoints:
 *   GET  /api/transactions/{txId}/deliveries         → { batches, summary, has_remaining }
 *   POST /api/transactions/{txId}/deliveries         → create new batch
 *   GET  /api/transactions/{txId}/deliveries/{n}/pdf → download PDF batch n
 */
function DeliveryBatchesSection({ txId, invoiceNo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  // Dialog untuk isi info sopir/plat sebelum cetak batch (bug-fix: sebelumnya
  // langsung download → backend menolak dengan 400 tanpa cara isi info dari UI).
  const [driverDialog, setDriverDialog] = useState(null); // { batch, defaultDriver, defaultPlate }

  const load = useCallback(async () => {
    if (!txId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/transactions/${txId}/deliveries`);
      setData(res);
    } catch (e) {
      setData({ batches: [], summary: [], has_remaining: false });
    } finally { setLoading(false); }
  }, [txId]);

  useEffect(() => { load(); }, [load]);

  const doDownload = async (batchIndex) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/transactions/${txId}/deliveries/${batchIndex}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let msg = 'Gagal cetak surat jalan';
      try {
        const err = await res.json();
        const d = err.detail;
        if (typeof d === 'string') msg = d;
        else if (d?.message) msg = d.message;
      } catch (_) { /* ignore */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `SuratJalan-${invoiceNo}-Batch${batchIndex}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  // Bug-fix: kalau batch belum punya driver_name/vehicle_plate, buka dialog input dulu.
  // Setelah user isi & simpan (PATCH batch), otomatis lanjut download PDF.
  const requestPrint = async (batch) => {
    const hasDriver = !!(batch.driver_name && batch.driver_name.trim());
    const hasPlate = !!(batch.vehicle_plate && batch.vehicle_plate.trim());
    if (hasDriver && hasPlate) {
      try { await doDownload(batch.batch_index); }
      catch (e) { toast.error(e.message || 'Gagal cetak'); }
      return;
    }
    // Buka dialog isi info sopir/plat
    setDriverDialog({
      batch,
      defaultDriver: batch.driver_name || '',
      defaultPlate: batch.vehicle_plate || '',
      defaultDate: batch.delivery_date || '',
    });
  };

  const saveDriverAndPrint = async ({ driver, plate, deliveryDate }) => {
    if (!driverDialog) return;
    const b = driverDialog.batch;
    try {
      await api.patch(`/transactions/${txId}/deliveries/${b.batch_index}`, {
        driver_name: driver, vehicle_plate: plate, delivery_date: deliveryDate || null,
      });
      setDriverDialog(null);
      await load();
      await doDownload(b.batch_index);
      toast.success('Info sopir tersimpan & Surat Jalan dicetak');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Gagal simpan info batch');
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border p-3 space-y-1 text-sm">
        <div className="text-xs text-muted-foreground">Memuat surat jalan...</div>
      </div>
    );
  }
  if (!data) return null;
  const batches = data.batches || [];
  const summary = data.summary || [];
  const hasRemaining = !!data.has_remaining;
  // Total qty summary
  const totalOrder = summary.reduce((a, s) => a + (s.quantity_total || 0), 0);
  const totalShipped = summary.reduce((a, s) => a + (s.quantity_shipped || 0), 0);

  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid="tx-deliveries-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-primary" />
          <div className="font-semibold text-sm">Surat Jalan (Delivery Order)</div>
          <Badge variant="secondary" className="tabular-nums">{batches.length} batch</Badge>
        </div>
        {hasRemaining && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="tx-create-batch-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Buat Surat Jalan Baru
              </Button>
            </DialogTrigger>
            <CreateBatchDialog
              txId={txId}
              summary={summary}
              onCreated={() => { setCreateOpen(false); load(); }}
            />
          </Dialog>
        )}
      </div>

      {/* Summary sudah dikirim vs sisa */}
      {summary.length > 0 && (
        <div className="rounded-md border bg-accent/30 p-2 text-xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-muted-foreground">Progress pengiriman</span>
            <span className="tabular-nums font-medium">
              <b>{totalShipped}</b> dari <b>{totalOrder}</b> btg dikirim
              {hasRemaining && <span className="text-amber-700 ml-2">• {totalOrder - totalShipped} btg belum dikirim</span>}
            </span>
          </div>
          <div className="space-y-0.5">
            {summary.map((s) => (
              <div key={s.product_id} className="flex items-center gap-2 justify-between">
                <span className="truncate">{s.name}</span>
                <span className="tabular-nums shrink-0">
                  <span className="text-emerald-700">{s.quantity_shipped}</span>
                  <span className="text-muted-foreground"> / {s.quantity_total}</span>
                  {s.quantity_remaining > 0 && (
                    <span className="ml-2 text-amber-700 font-medium">Sisa {s.quantity_remaining}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List batches */}
      {batches.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">Belum ada surat jalan tercatat (transaksi legacy).</div>
      ) : (
        <div className="space-y-1.5">
          {batches.map((b) => (
            <div key={b.batch_index} className="flex items-start justify-between gap-2 rounded-md border p-2 text-xs" data-testid={`tx-batch-row-${b.batch_index}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Batch #{b.batch_index}</Badge>
                  <span className="font-mono font-medium">{b.do_no}</span>
                  <span className="text-muted-foreground">{fmtDate(b.created_at)}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Dibuat oleh: <b className="text-foreground">{b.created_by_name || '-'}</b>
                  {b.driver_name && <> • Sopir: {b.driver_name}</>}
                  {b.vehicle_plate && <> • Plat: {b.vehicle_plate}</>}
                </div>
                <div className="text-[11px] mt-0.5">
                  {(b.items || []).map((it, i) => (
                    <span key={`${b.batch_index}-${it.product_id || it.sku || i}`} className="mr-2">
                      {it.name} <b className="tabular-nums">×{it.quantity}</b>
                    </span>
                  ))}
                </div>
                {b.notes && <div className="text-[11px] italic text-muted-foreground mt-0.5">"{b.notes}"</div>}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => requestPrint(b)}
                data-testid={`tx-batch-print-${b.batch_index}`}
              >
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak
              </Button>
            </div>
          ))}
        </div>
      )}

      {hasRemaining && batches.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <div><b>Kurang kirim:</b> masih ada barang belum dikirim. Buat Surat Jalan Baru untuk batch berikutnya. Total transaksi tidak berubah.</div>
        </div>
      )}

      {/* Dialog input info sopir & plat sebelum cetak (bug-fix). */}
      <Dialog open={!!driverDialog} onOpenChange={(o) => !o && setDriverDialog(null)}>
        {driverDialog && (
          <DriverInfoDialog
            batch={driverDialog.batch}
            defaultDriver={driverDialog.defaultDriver}
            defaultPlate={driverDialog.defaultPlate}
            defaultDate={driverDialog.defaultDate}
            onSave={saveDriverAndPrint}
          />
        )}
      </Dialog>
    </div>
  );
}

/** Dialog input info sopir/plat/tanggal sebelum cetak batch. */
function DriverInfoDialog({ batch, defaultDriver, defaultPlate, defaultDate, onSave }) {
  const [driver, setDriver] = useState(defaultDriver || '');
  const [plate, setPlate] = useState(defaultPlate || '');
  const [deliveryDate, setDeliveryDate] = useState(defaultDate || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!driver.trim() || !plate.trim()) {
      toast.error('Nama sopir dan plat kendaraan wajib diisi');
      return;
    }
    setSaving(true);
    try { await onSave({ driver: driver.trim(), plate: plate.trim(), deliveryDate }); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-md" data-testid="tx-driver-info-dialog">
      <DialogHeader>
        <DialogTitle>Info Pengiriman — Batch #{batch.batch_index}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="rounded-md border bg-blue-50 border-blue-200 p-2.5 text-xs text-blue-900">
          Nama sopir dan plat kendaraan wajib diisi sebelum cetak Surat Jalan.
          Info ini disimpan di batch ini dan tercetak di PDF.
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Nama Sopir *</Label>
          <Input value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="mis. Budi Santoso" data-testid="tx-driver-name-input" autoFocus />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Plat Kendaraan *</Label>
          <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="mis. B 1234 XYZ" data-testid="tx-driver-plate-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tanggal Kirim</Label>
          <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="tx-driver-date-input" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="tx-driver-save-print">
          <Printer className="h-4 w-4 mr-1.5" /> {saving ? 'Menyimpan...' : 'Simpan & Cetak'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// Export section supaya dipakai ulang oleh AdminDocuments (modul Cetak Dokumen).
export { DeliveryBatchesSection };

/**
 * Dialog untuk EDIT transaksi yang sudah tersimpan. Semua role bisa edit.
 * - Edit qty per item (angka bulat)
 * - Edit harga jual custom per item
 * - Edit include PPN per item
 * - Tambah item baru (search produk)
 * - Hapus item existing
 * - Edit metode bayar, notes, tempo, due date
 * - Wajib isi alasan (audit)
 * - Warning jika sudah ada pembayaran atau batch SJ > 1
 * Backend: PATCH /api/transactions/{id} dengan { items, payment_method, notes, ..., confirm_paid, confirm_batches, reason }
 */
function EditTransactionDialog({ tx, onClose, onSaved }) {
  const [items, setItems] = useState([]);          // [{product_id, sku, name, quantity, custom_unit_price, include_ppn}]
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const [products, setProducts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, requires: [...] }

  // Load tx into local state
  useEffect(() => {
    if (!tx) return;
    setItems((tx.items || []).map((it) => ({
      product_id: it.product_id,
      sku: it.sku,
      name: it.name,
      quantity: Number(it.quantity) || 1,
      custom_unit_price: it.manual_price ? Number(it.unit_price_before_ppn ?? it.unit_price) : null,
      include_ppn: !!it.include_ppn,
      original_price: Number(it.original_price) || Number(it.unit_price) || 0,
    })));
    setPaymentMethod(tx.payment_method || 'cash');
    setPaymentRef(tx.payment_reference || '');
    setNotes(tx.notes || '');
    setDueDate(tx.due_date || '');
    setReason('');
    setConfirmDialog(null);
  }, [tx]);

  // Load products for add-item
  useEffect(() => {
    if (!tx) return;
    (async () => {
      try {
        const { data } = await api.get('/products?active_only=false');
        setProducts(data || []);
      } catch (_) { /* silent */ }
    })();
  }, [tx]);

  const availableProducts = useMemo(() => {
    const existingIds = new Set(items.map((i) => i.product_id));
    const notInCart = products.filter((p) => !existingIds.has(p.id));
    if (!productQuery.trim()) return notInCart.slice(0, 20);
    const q = productQuery.toLowerCase();
    return notInCart.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [products, items, productQuery]);

  const addItem = (p) => {
    setItems((prev) => [...prev, {
      product_id: p.id,
      sku: p.sku,
      name: p.name,
      quantity: 1,
      custom_unit_price: null,
      include_ppn: false,
      original_price: Number(p.selling_price) || 0,
    }]);
    setProductQuery('');
    setSearchOpen(false);
  };

  const removeItem = (pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid));

  const updateItem = (pid, patch) => {
    setItems((prev) => prev.map((i) => (i.product_id === pid ? { ...i, ...patch } : i)));
  };

  const newTotal = useMemo(() => {
    return items.reduce((acc, i) => {
      const unit = i.custom_unit_price != null && i.custom_unit_price !== '' ? Number(i.custom_unit_price) : Number(i.original_price);
      const withPpn = i.include_ppn ? unit * 1.11 : unit;
      return acc + withPpn * (Number(i.quantity) || 0);
    }, 0);
  }, [items]);

  const submit = async (confirmFlags = {}) => {
    if (!reason.trim()) {
      toast.error('Alasan edit wajib diisi (untuk audit)');
      return;
    }
    if (items.length === 0) {
      toast.error('Item tidak boleh kosong');
      return;
    }
    // Validate qty integer positive
    for (const it of items) {
      const q = Number(it.quantity);
      if (!Number.isFinite(q) || q < 1 || !Number.isInteger(q)) {
        toast.error(`Qty untuk "${it.name}" harus angka bulat ≥ 1`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: Number(i.quantity),
          custom_unit_price: i.custom_unit_price != null && i.custom_unit_price !== '' ? Number(i.custom_unit_price) : null,
          include_ppn: !!i.include_ppn,
        })),
        payment_method: paymentMethod,
        payment_reference: paymentRef || null,
        notes: notes || null,
        due_date: paymentMethod === 'tempo' && dueDate ? dueDate : null,
        reason: reason.trim(),
        ...confirmFlags,
      };
      await api.patch(`/transactions/${tx.id}`, payload);
      toast.success('Transaksi berhasil diperbarui');
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Confirmation required flow (409)
      if (detail && typeof detail === 'object' && detail.code === 'confirm_required') {
        setConfirmDialog({
          message: detail.message,
          requires: detail.requires || [],
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Gagal update'));
      }
    } finally { setSaving(false); }
  };

  const proceedWithConfirmation = () => {
    if (!confirmDialog) return;
    const flags = {};
    confirmDialog.requires.forEach((r) => { flags[r] = true; });
    setConfirmDialog(null);
    submit(flags);
  };

  if (!tx) return null;
  const totalChange = newTotal - Number(tx.total || 0);

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tx-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Transaksi {tx.invoice_no}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {/* Info transaksi */}
          <div className="rounded-md border bg-accent/30 p-2.5 text-xs">
            <div className="flex justify-between">
              <span>Dibuat oleh: <b>{tx.created_by_name}</b> pada {fmtDate(tx.created_at)}</span>
              <span>Total sekarang: <b className="tabular-nums">{fmtIDR(tx.total)}</b></span>
            </div>
            {tx.paid_amount > 0 && (
              <div className="mt-1.5 rounded bg-amber-100 border border-amber-300 p-1.5 text-amber-800 text-[11px] flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Sudah dibayar: <b>{fmtIDR(tx.paid_amount)}</b>. Edit akan meminta konfirmasi.
              </div>
            )}
            {(tx.delivery_batches?.length || 0) > 1 && (
              <div className="mt-1.5 rounded bg-amber-100 border border-amber-300 p-1.5 text-amber-800 text-[11px] flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Sudah ada {tx.delivery_batches.length} batch Surat Jalan. Adjust qty di sini tidak update SJ otomatis.
              </div>
            )}
          </div>

          {/* Items table */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-semibold">Item ({items.length})</Label>
              <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="tx-edit-add-item-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Tambah Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="tx-edit-add-item-dialog">
                  <DialogHeader><DialogTitle>Pilih Produk</DialogTitle></DialogHeader>
                  <Input
                    autoFocus
                    placeholder="Cari nama atau SKU..."
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    data-testid="tx-edit-product-search"
                  />
                  <div className="max-h-60 overflow-y-auto border rounded divide-y">
                    {availableProducts.length === 0 ? (
                      <div className="p-3 text-xs text-muted-foreground text-center">Tidak ada produk.</div>
                    ) : availableProducts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addItem(p)}
                        className="w-full text-left p-2 text-xs hover:bg-accent/40 flex justify-between items-center"
                        data-testid={`tx-edit-add-${p.id}`}
                      >
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{p.sku} · stok {p.stock}</div>
                        </div>
                        <div className="tabular-nums">{fmtIDR(p.selling_price)}</div>
                      </button>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Produk</TableHead>
                    <TableHead className="text-xs w-16 text-right">Qty</TableHead>
                    <TableHead className="text-xs w-28 text-right">Harga/unit</TableHead>
                    <TableHead className="text-xs w-14 text-center">PPN</TableHead>
                    <TableHead className="text-xs w-24 text-right">Subtotal</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((i) => {
                    const unit = i.custom_unit_price != null && i.custom_unit_price !== '' ? Number(i.custom_unit_price) : Number(i.original_price);
                    const withPpn = i.include_ppn ? unit * 1.11 : unit;
                    const sub = withPpn * (Number(i.quantity) || 0);
                    return (
                      <TableRow key={i.product_id} data-testid={`tx-edit-row-${i.product_id}`}>
                        <TableCell className="text-xs">
                          <div className="font-medium">{i.name}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{i.sku}</div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="1" step="1" inputMode="numeric"
                            value={i.quantity}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') return updateItem(i.product_id, { quantity: '' });
                              const n = parseInt(v, 10);
                              if (!isNaN(n) && n >= 0) updateItem(i.product_id, { quantity: n });
                            }}
                            className="h-7 text-xs tabular-nums text-right"
                            data-testid={`tx-edit-qty-${i.product_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0" step="1"
                            value={i.custom_unit_price ?? ''}
                            placeholder={String(Math.round(i.original_price))}
                            onChange={(e) => updateItem(i.product_id, { custom_unit_price: e.target.value === '' ? null : Number(e.target.value) })}
                            className="h-7 text-xs tabular-nums text-right"
                            data-testid={`tx-edit-price-${i.product_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            checked={i.include_ppn}
                            onChange={(e) => updateItem(i.product_id, { include_ppn: e.target.checked })}
                            data-testid={`tx-edit-ppn-${i.product_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{fmtIDR(sub)}</TableCell>
                        <TableCell>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-rose-600"
                            onClick={() => removeItem(i.product_id)}
                            title="Hapus item"
                            data-testid={`tx-edit-remove-${i.product_id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="mt-2 flex justify-between text-xs px-1">
              <span className="text-muted-foreground">Total lama: <b className="tabular-nums text-foreground">{fmtIDR(tx.total)}</b></span>
              <span>
                Total baru: <b className="tabular-nums" data-testid="tx-edit-new-total">{fmtIDR(newTotal)}</b>
                {' '}
                <span className={totalChange > 0 ? 'text-emerald-700' : totalChange < 0 ? 'text-rose-700' : 'text-muted-foreground'}>
                  ({totalChange > 0 ? '+' : ''}{fmtIDR(totalChange)})
                </span>
              </span>
            </div>
          </div>

          {/* Payment & notes */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Metode Pembayaran</Label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full h-8 px-2 text-xs rounded-md border bg-background" data-testid="tx-edit-payment-method">
                <option value="cash">Cash</option>
                <option value="transfer">Transfer</option>
                <option value="qris">QRIS</option>
                <option value="tempo">Tempo</option>
              </select>
            </div>
            {paymentMethod === 'tempo' && (
              <div className="space-y-1">
                <Label className="text-xs">Jatuh Tempo</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-8 text-xs" data-testid="tx-edit-due-date" />
              </div>
            )}
            {(paymentMethod === 'transfer' || paymentMethod === 'qris') && (
              <div className="space-y-1">
                <Label className="text-xs">Referensi Pembayaran</Label>
                <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className="h-8 text-xs" data-testid="tx-edit-payment-ref" />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Catatan</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-8 text-xs" data-testid="tx-edit-notes" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alasan edit * <span className="text-muted-foreground">(wajib untuk audit)</span></Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="mis. Customer minta tambah barang, koreksi qty"
              className="h-8 text-xs"
              data-testid="tx-edit-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={() => submit()} disabled={saving || !reason.trim()} data-testid="tx-edit-save">
            {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
          </Button>
        </DialogFooter>

        {/* Confirmation sub-dialog untuk paid_amount > 0 atau batches > 1 */}
        <AlertDialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
          <AlertDialogContent data-testid="tx-edit-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Konfirmasi Edit</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDialog?.message}
                <br /><br />
                <b>Yakin lanjutkan?</b> Stok otomatis di-adjust. Perubahan tercatat di audit log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction onClick={proceedWithConfirmation} data-testid="tx-edit-confirm-btn">
                Ya, Lanjutkan
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog list riwayat edit transaksi (audit log). */
function EditHistoryDialog({ tx, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!tx) return;
    setLoading(true);
    api.get(`/transactions/${tx.id}/edit-history`)
      .then(({ data }) => setEntries(data.history || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [tx]);

  if (!tx) return null;
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tx-history-dialog">
        <DialogHeader>
          <DialogTitle>Riwayat Edit — {tx.invoice_no}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-4 text-xs text-muted-foreground">Memuat...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-4 text-xs text-muted-foreground">Belum ada riwayat edit.</div>
        ) : (
          <div className="space-y-2">
            {entries.map((e, i) => {
              // Gunakan kombinasi edited_at + edited_by supaya stable meski array reorder.
              // Fallback ke `edit-${i}` kalau field tak lengkap (backward compat).
              const key = e.edited_at && e.edited_by ? `${e.edited_at}-${e.edited_by}` : `edit-${i}`;
              return (
              <div key={key} className="rounded-md border p-2.5 text-xs space-y-1" data-testid={`tx-history-entry-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="font-medium">
                    Dirubah oleh <b className="text-primary">{e.edited_by_name}</b>
                    <Badge variant="outline" className="ml-1.5 text-[9px] uppercase">{e.edited_by_role}</Badge>
                  </div>
                  <div className="text-muted-foreground text-[10px]">{fmtDate(e.edited_at)}</div>
                </div>
                {e.reason && <div className="italic text-muted-foreground">Alasan: "{e.reason}"</div>}
                <div className="mt-1 space-y-0.5">
                  {Object.entries(e.changes || {}).map(([field, change]) => {
                    if (field === 'items') {
                      return (
                        <div key={field}>
                          <div className="font-medium">Item:</div>
                          {(change || []).map((c) => (
                            <div key={c.product_id} className="ml-3 text-[11px]">
                              • {c.name}: <span className="tabular-nums">{c.qty_before}</span> → <b className="tabular-nums text-primary">{c.qty_after}</b>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    if (field === 'total') {
                      return (
                        <div key={field} className="text-[11px]">
                          <b>Total:</b> <span className="tabular-nums">{fmtIDR(change.before)}</span> → <b className="tabular-nums text-primary">{fmtIDR(change.after)}</b>
                        </div>
                      );
                    }
                    return (
                      <div key={field} className="text-[11px]">
                        <b>{field}:</b> <span className="text-muted-foreground">{String(change.before ?? '—')}</span> → <b>{String(change.after ?? '—')}</b>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Dialog untuk buat batch pengiriman baru (Surat Jalan tambahan). */
function CreateBatchDialog({ txId, summary, onCreated }) {
  // qty per product; default = sisa penuh (kirim semua sekarang)
  const initial = useMemo(() => {
    const o = {};
    summary.forEach((s) => { if (s.quantity_remaining > 0) o[s.product_id] = s.quantity_remaining; });
    return o;
  }, [summary]);
  const [qtys, setQtys] = useState(initial);
  const [notes, setNotes] = useState('');
  const [driver, setDriver] = useState('');
  const [plate, setPlate] = useState('');
  const [saving, setSaving] = useState(false);

  const totalQty = Object.values(qtys).reduce((a, v) => a + (Number(v) || 0), 0);

  const submit = async () => {
    const items = Object.entries(qtys)
      .map(([product_id, q]) => ({ product_id, quantity: Number(q) || 0 }))
      .filter((x) => x.quantity > 0);
    if (items.length === 0) return toast.error('Isi minimal 1 item dengan qty > 0');
    setSaving(true);
    try {
      await api.post(`/transactions/${txId}/deliveries`, {
        items, notes: notes || null,
        driver_name: driver || null,
        vehicle_plate: plate || null,
      });
      toast.success('Surat Jalan baru dibuat');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal buat batch'); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="tx-create-batch-dialog">
      <DialogHeader>
        <DialogTitle>Buat Surat Jalan Baru (Batch Selanjutnya)</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        <div className="text-xs text-muted-foreground">
          Isi jumlah yang akan dikirim di batch ini. Nomor Surat Jalan akan otomatis di-generate.
          Total transaksi <b>tidak akan berubah</b>.
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Barang</TableHead>
                <TableHead className="text-xs text-right">Sisa</TableHead>
                <TableHead className="text-xs text-right w-28">Kirim</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.filter((s) => s.quantity_remaining > 0).map((s) => (
                <TableRow key={s.product_id}>
                  <TableCell className="text-xs">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{s.sku}</div>
                  </TableCell>
                  <TableCell className="text-xs text-right tabular-nums text-amber-700">{s.quantity_remaining}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number" min="0" max={s.quantity_remaining} step="1"
                      value={qtys[s.product_id] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? '' : Math.max(0, Math.min(s.quantity_remaining, Math.floor(Number(e.target.value) || 0)));
                        setQtys((prev) => ({ ...prev, [s.product_id]: v }));
                      }}
                      className="h-8 text-xs tabular-nums text-right"
                      data-testid={`tx-batch-qty-${s.product_id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Nama Sopir (opsional)</Label>
            <Input value={driver} onChange={(e) => setDriver(e.target.value)} className="h-8 text-xs" data-testid="tx-batch-driver" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Plat Kendaraan (opsional)</Label>
            <Input value={plate} onChange={(e) => setPlate(e.target.value)} className="h-8 text-xs" data-testid="tx-batch-plate" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Catatan Batch (opsional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="mis. Truk kedua siang hari" className="h-8 text-xs" data-testid="tx-batch-notes" />
        </div>
      </div>
      <DialogFooter>
        <div className="mr-auto text-xs text-muted-foreground self-center">
          Total kirim batch ini: <b className="tabular-nums text-foreground">{totalQty}</b> btg
        </div>
        <Button onClick={submit} disabled={saving || totalQty === 0} data-testid="tx-batch-save">
          {saving ? 'Menyimpan...' : 'Simpan Batch'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
